import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';

import { handleApiCall } from '@/api/utils/handleApiCall';
import { MutationStateFns } from '@/api/utils/mutation';
import { useAuth } from '@/context/AuthProvider';
import { usePropertyManager } from '@/context/PropertyManagerProvider';

import { QUERY_KEY as GET_ALL_QUERY_KEY } from './useGetAllContacts';

interface DeleteContactParams {
  contactId: number;
}

const ENDPOINT_PREFIX = 'contacts';
const METHOD = 'DELETE';

export const useDeleteContact = (stateFns?: MutationStateFns<void>) => {
  const { session } = useAuth();
  const { selectedPropertyLocation } = usePropertyManager();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ contactId }: DeleteContactParams): Promise<void> => {
      if (!selectedPropertyLocation?.id) {
        throw new Error('No location selected');
      }

      return handleApiCall<void, never>(
        session,
        METHOD,
        `${ENDPOINT_PREFIX}/${contactId}`,
        undefined,
        {
          params: {
            location_id: selectedPropertyLocation.id,
          },
        }
      );
    },
    onSuccess: () => {
      enqueueSnackbar('Contact deleted successfully', { variant: 'success' });

      // Invalidate contacts list
      queryClient.invalidateQueries({
        queryKey: GET_ALL_QUERY_KEY(selectedPropertyLocation?.id),
      });

      stateFns?.onSuccess?.();
    },
    onError: (err: AxiosError) => {
      enqueueSnackbar('Failed to delete contact', { variant: 'error' });
      stateFns?.onError?.(err);
    },
  });
};
